import { dueDate } from './planning'
import { tierRank } from './rebook-sort'
import type { Customer } from './types'

// Clickable column sorting for the grid, shared by the planning sheet and the
// full sheet. Pure data — no DOM here — so the sorter can be tested headlessly.
//
// Rules the sheet depends on:
// - Sorting never mutates the input records (rowsToShow feeds straight off the
//   customer map; a sort that reordered the map would scramble edit targets).
// - Empty is LAST, whichever direction: a blank cell never jumps to the top
//   when you reverse a column, because "no data" is not a value.
// - Ties break on customer id, so the same data always renders in the same
//   order and a re-render cannot shuffle rows under the user's cursor.
// - Dates sort chronologically (ISO yyyy-mm-dd orders correctly as text);
//   price and cycle sort numerically; text sorts with natural collation, so
//   "10 " comes after "9" and case/accents do not decide.

export type SortDir = 'asc' | 'desc'

// 'due' is the dropdown's default and keeps its long-standing meaning — the
// next booked visit if there is one, otherwise the cycle-computed due date.
// 'due_cycle' is the Due COLUMN: only the cycle computation, so it stays
// distinct from the Next visit column ('next_booked'). The two ideas must
// never silently sort by each other's data.
const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })

// Blank / whitespace-only text is "no value", not "a value that sorts first".
function textValue(v: string | null | undefined): string | null {
  const t = (v ?? '').trim()
  return t === '' ? null : t
}

// The area a row is grouped by — same rule the Area filter uses. This is the
// DISPLAY label only: sorting never sorts on it, because 'Not set' is a
// caption for people, not a value — sorting on it would fling every blank to
// the top on a reverse. The sort keys below return null for absent data.
export function areaOf(c: Customer): string {
  return c.area?.trim() || c.postcode?.trim().split(/\s/)[0].toUpperCase() || 'Not set'
}

// The full-sheet Area column sorts its own visible values: the raw trimmed
// area, and nothing else. A customer with no area but a postcode has an empty
// Area cell, so an area-key sort treats it as empty (last, both directions) —
// it must not borrow the postcode's outcode, which the customer never typed.
function rawArea(c: Customer): string | null {
  return textValue(c.area)
}

// The planning sheet's Area / postcode column shows the grouped location
// (area, else the postcode outcode) — its sort key matches what it displays,
// falls back through the same chain, and is null when BOTH are absent.
function locationValue(c: Customer): string | null {
  const area = textValue(c.area)
  if (area) return area
  const pc = c.postcode?.trim()
  return pc ? pc.split(/\s/)[0].toUpperCase() : null
}

// Column field → sort key. 'actions' and anything unknown return null: the
// Booking column gets a plain header, never a pretend-sortable button.
export function sortKeyForColumn(field: string): string | null {
  switch (field) {
    case 'name': return 'name'
    case 'area': return 'area'
    case 'location': return 'location'
    case 'address': return 'address'
    case 'postcode': return 'postcode'
    case 'phone': return 'phone'
    case 'cycle_days': return 'cycle_days'
    case 'reg_price_pence': return 'price'
    case 'last_clean': return 'last_clean'
    case 'next_booked': return 'next_booked'
    case 'due': return 'due_cycle'
    case 'status': return 'status'
    case 'to_call': return 'to_call'
    case 'maybe_lapsed': return 'maybe_lapsed'
    case 'notes': return 'notes'
    // The extra columns the Work sheet can add (grid.ts EXTRA_COLS). Each
    // sorts on its own visible value, so a header the owner turned on behaves
    // exactly like the ones that were always there.
    case 'tier': return 'tier'
    case 'origin': return 'origin'
    case 'job_notes': return 'job_notes'
    case 'first_clean_price_pence': return 'first_price'
    case 'inside_price_pence': return 'inside_price'
    case 'time_taken': return 'time_taken'
    case 'call_notes': return 'call_notes'
    case 'payment_increase': return 'payment_increase'
    case 'preferred_times': return 'preferred_times'
    case 'parking_restrictions': return 'parking_restrictions'
    default: return null
  }
}

function sortValue(c: Customer, key: string): string | number | null {
  switch (key) {
    // Original/insertion order: the immutable numeric id, NEVER a date. Every
    // imported row shares one import timestamp and last-clean dates describe
    // work history, not when the row was added — sorting on either would lie
    // about "order added". Imported ids follow the Work list, then the lapsed
    // To Delete rows; customers created in the app append after, so id order
    // IS sheet order: ascending = original (oldest first), descending = newest
    // added first.
    case 'added': return c.id
    case 'name': return textValue(c.name)
    case 'area': return rawArea(c)
    case 'location': return locationValue(c)
    case 'address': return textValue(c.address)
    case 'postcode': return textValue(c.postcode)
    case 'phone': return textValue(c.phone)
    case 'notes': return textValue(c.notes)
    // A cycle of 0 is no cycle — same rule dueDate applies before computing.
    case 'cycle_days': return c.cycle_days && c.cycle_days > 0 ? c.cycle_days : null
    case 'price': return c.reg_price_pence ?? null
    case 'last_clean': return textValue(c.last_clean)
    case 'next_booked': return textValue(c.next_booked)
    case 'due': return textValue(c.next_booked || dueDate(c))
    case 'due_cycle': return dueDate(c)
    case 'status': return textValue(c.status)
    case 'to_call': return c.to_call ?? null
    case 'maybe_lapsed': return c.maybe_lapsed ?? null
    // Tier sorts on the NUMBER in the box, the same rule the Tier badge reads
    // with — "3" and "3.0" are one tier, and a box holding a sentence ("havent
    // had for AGES") has no tier at all, so it sorts as empty (last, both
    // directions) rather than as tierRank's 99, which desc would fling first.
    case 'tier': { const n = tierRank(c.tier); return n === 99 ? null : n }
    case 'origin': return textValue(c.origin)
    case 'job_notes': return textValue(c.job_notes)
    case 'first_price': return c.first_clean_price_pence ?? null
    case 'inside_price': return c.inside_price_pence ?? null
    case 'time_taken': return textValue(c.time_taken)
    case 'call_notes': return textValue(c.call_notes)
    case 'payment_increase': return textValue(c.payment_increase)
    case 'preferred_times': return textValue(c.preferred_times)
    case 'parking_restrictions': return textValue(c.parking_restrictions)
    default: return null
  }
}

// Nulls last in BOTH directions, so the null result is never flipped by the
// direction the way a real comparison is.
export function compareValues(a: string | number | null, b: string | number | null, dir: SortDir): number {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  const cmp =
    typeof a === 'number' && typeof b === 'number'
      ? a < b ? -1 : a > b ? 1 : 0
      : collator.compare(String(a), String(b))
  return dir === 'desc' ? -cmp : cmp
}

export function sortCustomers(rows: Customer[], key: string, dir: SortDir): Customer[] {
  return [...rows].sort((a, b) => compareValues(sortValue(a, key), sortValue(b, key), dir) || a.id - b.id)
}
